import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, UPI } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import { resolveAllowedGateways } from '../../../utils/flightFare'
import loadScript from '../../../utils/loadScript'
import { getRazorpayKeyId, getStripePublishableKey, findStripePublishableKeyInPayload } from '../../../config/stripeEnv'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const GATEWAYS = [
    { key: 'razorpay', label: 'Razorpay', subtitle: 'UPI, Cards, NetBanking, Wallets', icon: UPI },
    { key: 'stripe', label: 'Card / Stripe', subtitle: 'International cards', icon: Card },
]

/** Pull gateway checkout fields (razorpay order / stripe secret) from /subscriptions/subscribe response */
const extractCheckoutInfo = (payload = {}) => {
    const root = payload || {}
    const candidates = [root, root.checkout, root.payment, root.data, root.data?.checkout].filter(Boolean)

    let orderId = ''
    let razorpayKey = ''
    let clientSecret = ''
    let publishableKey = ''
    let gateway = ''
    let subscriptionId = ''

    candidates.forEach((item) => {
        if (!orderId) orderId = item.order_id || item.razorpay_order_id || ''
        if (!razorpayKey) razorpayKey = item.key || item.razorpay_key || ''
        if (!clientSecret) {
            clientSecret = item.client_secret
                || item.clientSecret
                || item.payment_intent_client_secret
                || item.stripe_client_secret
                || ''
        }
        if (!publishableKey) {
            publishableKey = item.publishable_key
                || item.stripe_publishable_key
                || item.public_key
                || item.publishableKey
                || ''
        }
        if (!gateway) gateway = String(item.gateway || '').toLowerCase()
        if (!subscriptionId) subscriptionId = item.subscription_id || item.id || ''
    })

    return {
        orderId: String(orderId || '').trim(),
        razorpayKey: String(razorpayKey || '').trim(),
        clientSecret: String(clientSecret || '').trim(),
        publishableKey: String(publishableKey || '').trim(),
        gateway: gateway || (orderId ? 'razorpay' : (clientSecret ? 'stripe' : '')),
        subscriptionId,
    }
}

const SubscriptionPayment = ({ selection, onBack, onSuccess }) => {
    const plan = selection?.plan || null
    const billingCycle = selection?.billingCycle || 'monthly'
    const currency = selection?.currency || plan?.currency || 'INR'

    const [gateway, setGateway] = useState(null)
    const [paying, setPaying] = useState(false)

    const [checkout, setCheckout] = useState(null)
    const [checkoutLoading, setCheckoutLoading] = useState(false)
    const [checkoutError, setCheckoutError] = useState('')

    const [stripeReady, setStripeReady] = useState(false)
    const [stripeCardError, setStripeCardError] = useState('')

    const stripeRef = useRef(null)
    const elementsRef = useRef(null)
    const paymentElementRef = useRef(null)
    const mountTokenRef = useRef(0)

    const razorpayKeyId = getRazorpayKeyId()

    const availableGateways = useMemo(() => {
        const allowedKeys = resolveAllowedGateways(currency, selection?.allowedGateways)
        return GATEWAYS.filter((g) => allowedKeys.includes(g.key))
    }, [currency, selection?.allowedGateways])

    useEffect(() => {
        if (!availableGateways.length) return
        if (!gateway || !availableGateways.some((g) => g.key === gateway)) {
            setGateway(availableGateways[0].key)
        }
    }, [availableGateways, gateway])

    // Create the subscription checkout (order_id / client_secret) as soon as a
    // gateway is picked, so Stripe's card form has something to mount against
    // before the user clicks Pay.
    useEffect(() => {
        if (!plan || !gateway) return
        let mounted = true
        setCheckoutLoading(true)
        setCheckoutError('')
        setCheckout(null)

        service.post(endpoints.subscriptions.subscribe, {
            plan_type: plan.plan_type,
            billing_cycle: billingCycle,
            gateway,
            currency,
        }, { timeout: 45000, silent: true })
            .then(({ data }) => {
                if (!mounted) return
                setCheckout(extractCheckoutInfo(data?.data ?? data))
            })
            .catch((error) => {
                if (!mounted) return
                setCheckoutError(error?.response?.data?.message || error?.message || 'Could not start checkout.')
            })
            .finally(() => {
                if (mounted) setCheckoutLoading(false)
            })

        return () => { mounted = false }
    }, [plan, gateway, billingCycle, currency])

    const stripeKey = getStripePublishableKey() || checkout?.publishableKey || findStripePublishableKeyInPayload(checkout)

    const destroyStripeElements = () => {
        if (paymentElementRef.current) {
            try {
                paymentElementRef.current.unmount()
            } catch {
                // ignore unmount races
            }
            paymentElementRef.current = null
        }
        elementsRef.current = null
        setStripeReady(false)
    }

    // Mount the Stripe Payment Element once the subscribe call has handed back
    // a client_secret for this checkout attempt.
    useEffect(() => {
        if (gateway !== 'stripe' || !checkout?.clientSecret) {
            destroyStripeElements()
            return undefined
        }

        let cancelled = false
        const token = ++mountTokenRef.current

        const mountStripe = async () => {
            setStripeReady(false)
            setStripeCardError('')

            if (!stripeKey) {
                setStripeCardError('Stripe publishable key not configured for this environment.')
                return
            }

            try {
                const Stripe = await loadScript('https://js.stripe.com/v3/', 'Stripe')
                if (cancelled || token !== mountTokenRef.current) return
                stripeRef.current = Stripe(stripeKey)

                destroyStripeElements()
                if (cancelled || token !== mountTokenRef.current) return

                await new Promise((r) => window.setTimeout(r, 50))
                if (cancelled || token !== mountTokenRef.current) return

                const mountEl = document.getElementById('subscription-payment-element')
                if (!mountEl) {
                    if (!cancelled) setStripeCardError('Payment form container not ready. Refresh and try again.')
                    return
                }
                mountEl.innerHTML = ''
                const elements = stripeRef.current.elements({
                    clientSecret: checkout.clientSecret,
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: '#026D4D',
                            borderRadius: '12px',
                            fontFamily: 'Onest, system-ui, sans-serif',
                        },
                    },
                })
                elementsRef.current = elements
                const paymentElement = elements.create('payment', {
                    layout: { type: 'tabs', defaultCollapsed: false },
                    paymentMethodOrder: ['card'],
                    fields: { billingDetails: 'auto' },
                })
                paymentElement.mount('#subscription-payment-element')
                paymentElement.on('change', (event) => {
                    if (!cancelled) setStripeCardError(event.error ? event.error.message : '')
                })
                paymentElement.on('ready', () => {
                    if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
                })
                paymentElementRef.current = paymentElement

                window.setTimeout(() => {
                    if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
                }, 800)
            } catch (error) {
                if (!cancelled) {
                    setStripeCardError(error?.message || 'Failed to load Stripe card form.')
                    setStripeReady(false)
                }
            }
        }

        mountStripe()

        return () => {
            cancelled = true
            destroyStripeElements()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateway, checkout?.clientSecret, stripeKey])

    if (!plan) {
        return (
            <div className='manageSubscription'>
                <div className='tripDocHeader' onClick={onBack}>
                    <BackArrow />
                    <span className='bsSectionTitle'>Subscription Payment</span>
                </div>
                <p className='securityMenuItem__subtitle mt-3'>No plan selected. Please choose a plan first.</p>
            </div>
        )
    }

    const priceLabel = `${plan.currency_symbol || ''}${plan.display_price ?? ''}${plan.price_suffix || ''}`

    const payWithRazorpay = async () => {
        const key = checkout?.razorpayKey || razorpayKeyId
        const orderId = checkout?.orderId
        if (!key) throw new Error('Razorpay key is missing from checkout response.')
        if (!orderId) throw new Error('Razorpay order_id missing from checkout response.')

        const Razorpay = await loadScript('https://checkout.razorpay.com/v1/checkout.js', 'Razorpay')

        const response = await new Promise((resolve, reject) => {
            let settled = false
            const finish = (fn, value) => {
                if (settled) return
                settled = true
                fn(value)
            }
            try {
                const rzp = new Razorpay({
                    key,
                    name: 'TravelGo',
                    description: `${plan.name} subscription`,
                    order_id: orderId,
                    theme: { color: '#15966f' },
                    handler(res) {
                        finish(resolve, res)
                    },
                    modal: {
                        ondismiss() {
                            if (!settled) finish(reject, new Error('Razorpay closed before payment finished.'))
                        },
                    },
                })
                rzp.on('payment.failed', (res) => {
                    const description = res?.error?.description || res?.error?.reason || 'Razorpay payment failed.'
                    finish(reject, new Error(description))
                })
                window.setTimeout(() => {
                    try {
                        rzp.open()
                    } catch (error) {
                        finish(reject, error instanceof Error ? error : new Error(String(error)))
                    }
                }, 0)
            } catch (error) {
                finish(reject, error instanceof Error ? error : new Error(String(error)))
            }
        })

        await service.post(endpoints.subscriptions.verifyRazorpay, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            plan_type: plan.plan_type,
            billing_cycle: billingCycle,
        }, { timeout: 45000 })
    }

    const payWithStripe = async () => {
        if (!stripeRef.current) throw new Error('Stripe SDK failed to initialize.')
        if (!elementsRef.current || !checkout?.clientSecret) throw new Error('Stripe card form is still loading.')
        if (!stripeReady) throw new Error('Stripe card form is still loading.')

        const result = await stripeRef.current.confirmPayment({
            elements: elementsRef.current,
            redirect: 'if_required',
        })
        if (result.error) throw new Error(result.error.message || 'Payment failed.')
        const intent = result.paymentIntent
        if (!intent || !['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
            throw new Error(`Stripe status: ${intent?.status || 'unknown'}`)
        }
    }

    const handlePayClick = async () => {
        if (paying || !gateway) return
        if (checkoutLoading || !checkout) {
            showToast('Checkout is still preparing, please wait.', 'error')
            return
        }
        setPaying(true)
        try {
            if (gateway === 'razorpay') {
                await payWithRazorpay()
            } else if (gateway === 'stripe') {
                await payWithStripe()
            } else {
                showToast('Select Razorpay or Stripe.', 'error')
                return
            }
            showToast('Subscription activated successfully', 'success')
            onSuccess?.()
        } catch (error) {
            showToast(error?.message || 'Payment failed.', 'error')
        } finally {
            setPaying(false)
        }
    }

    return (
        <div className='manageSubscription'>
            <div className='tripDocHeader' onClick={onBack}>
                <BackArrow />
                <span className='bsSectionTitle'>Subscription Payment</span>
            </div>

            <div className='manageSubCurrentPlan paddingCommon20 borderRadius20 mt-3'>
                <div className='manageSubCurrentPlan__head'>
                    <span className='manageSubCurrentPlan__icon'>
                        <img src={plan.icon} alt="" />
                    </span>
                    <div className='manageSubCurrentPlan__headInfo'>
                        <div className='manageSubCurrentPlan__name'>{plan.name}</div>
                        <div className='manageSubCurrentPlan__price'>{priceLabel}</div>
                    </div>
                    <span className='manageSubCurrentPlan__badge'>{billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                </div>
            </div>

            <div className='bsSectionTitle mt-4'>Payment</div>
            <div className='paymentMethodBox'>
                <div className='paymentMethodList'>
                    {availableGateways.map((gw) => (
                        <div
                            key={gw.key}
                            className={`paymentMethodItem ${gateway === gw.key ? 'active' : ''}`}
                            onClick={() => setGateway(gw.key)}
                        >
                            <span className='paymentMethodIcon'>
                                <img src={gw.icon} alt={gw.label} />
                            </span>
                            <div>
                                <div className='paymentMethodTitle'>{gw.label}</div>
                                <div className='paymentMethodSub'>{gw.subtitle}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className='paymentMethodPanel'>
                    {gateway === 'stripe' ? (
                        <>
                            <div className='bsSectionTitle'>Enter your card details</div>
                            {stripeKey ? (
                                <div className='paymentStripeCardWrap'>
                                    <div id='subscription-payment-element' className='paymentStripeCard' />
                                    {stripeCardError && <p className='bsFareNote'>{stripeCardError}</p>}
                                    {!stripeReady && !stripeCardError && (
                                        <p className='securityMenuItem__subtitle'>Loading secure card form...</p>
                                    )}
                                </div>
                            ) : (
                                <div className='paymentInfoBanner'>
                                    Stripe key not loaded. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to `.env` and restart the app.
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className='bsSectionTitle'>Pay via Razorpay</div>
                            <p className='paymentQrDesc'>
                                UPI, cards, net banking and wallets — pick your preferred option in the next step.
                            </p>
                        </>
                    )}

                    {checkoutError && <div className='paymentInfoBanner'>{checkoutError}</div>}

                    <button
                        type='button'
                        className='commonBtn w-100'
                        onClick={handlePayClick}
                        disabled={paying || checkoutLoading || !gateway || (gateway === 'stripe' && !stripeReady)}
                    >
                        {paying ? (
                            <>
                                <span className='btnSpinner' aria-hidden="true" />
                                Processing…
                            </>
                        ) : checkoutLoading ? (
                            'Preparing checkout...'
                        ) : (
                            `Pay ${priceLabel}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SubscriptionPayment
